import type { ReactNode } from 'react';
import type { PlantStatus } from '@/shared/data-types';
import { InverterIcon, InverterStatusDot } from './inverter-icon';
import { OptionCard } from './option-card';

/**
 * 인버터 행 카드 — `OptionCard` 위에 **아이콘 타일 · 이름(+상태 점) · 부제** 배치를 고정한 조합. 인버터 행을
 * 그리는 화면(현재 대시보드 인버터 구성 목록 `widgets/dashboard`)이 같은 행 디자인을 쓰도록 조립을 여기서 한 번만 한다.
 *
 * 부제·우측(`trailing`)은 화면이 채운다 — 대시보드는 하단 지표 줄(설비용량·현재 출력·오늘 발전량·오늘 발전시간)과 통신끊김 표기.
 * 데이터 포맷(용량·출력 문자열)은 도메인 어휘라 **호출처가 만들어 `subtitle` 로 넘긴다**.
 */
export interface InverterOptionCardProps {
  /** 인버터 이름 — 넘치면 말줄임. */
  name: string;
  /** 상태 — 이름 옆 컬러 점. 미상(undefined)이면 점 없음. */
  status?: PlantStatus;
  /** 부제 — 호출처가 조립한 지표(폭이 좁으면 여러 줄로 감길 수 있다). */
  subtitle: ReactNode;
  /** 좌측 슬롯 override — 기본은 인버터 아이콘 타일(전체/집계 행처럼 다른 아이콘이 필요할 때만 전달). */
  leading?: ReactNode;
  /** 우측 슬롯 — 상태 문구(대시보드 통신끊김) · 선택 표시 등. */
  trailing?: ReactNode;
  /** 선택 상태 — primary 테두리 + 틴트. 선택 개념이 없는 화면은 미전달. */
  selected?: boolean;
}

export function InverterOptionCard({
  name,
  status,
  subtitle,
  leading,
  trailing,
  selected,
}: InverterOptionCardProps) {
  return (
    <OptionCard
      className="p-2"
      selected={selected}
      leading={leading ?? <InverterIcon />}
      trailing={trailing}
    >
      <span className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-foreground">{name}</span>
          <InverterStatusDot status={status} />
        </span>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      </span>
    </OptionCard>
  );
}
